import type { StringifiedStates } from "@el8/scribe-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SCRIBE_RECORDER_TIMESLICE_MS } from "../constants";
import { scribeLogger } from "../logger";
import {
  useAudioContext,
  useAudioRecordingComponents,
  useMediaStream,
  useMicrophoneDeviceId,
  useOnStepChange,
  useScribeStore,
  useSetupScribe,
} from "../stores/scribe";
import { NablaAPILanguage, type NablaTranscriptItem } from "../types";
import { PromiseOrchestrator } from "../utils/async/PromiseOrchestrator";
import { processScribeRecorderData } from "../utils/audio";
import { makeThrottledLogError } from "../utils/throttledLogging";
import type { UseScribeOptions } from "./scribeSessionTypes";
import useSyncedRef from "./useSyncedRef";
import { useTranscriptItems } from "./useTranscriptItems";

const throttledLogError = makeThrottledLogError(scribeLogger, 60 * 1000);

export function useScribe(options: UseScribeOptions) {
  const { websocketUrl, getScribingSessionId, saveTranscriptItem, lastOffset } =
    options;

  useMicrophoneDeviceId();
  useMediaStream();

  const language = NablaAPILanguage.English;
  // TEMP HACK! There's a known issue with the scribe client where calling `client.open`
  // while it's in the `readyToClose` state causes it to unexpectedly enter a closed
  // state and never reach the open state, which causes Resume Recording to fail to
  // transcribe. Remove this once the underlying issue is fixed.
  const [isSafeToResume, setIsSafeToResume] = useState(true);

  // We're circumventing a race condition where the user hits pause BEFORE we establish our
  // WSS connection. This causes the re-connect on resume to reuse a scribing session ID that
  // has already been "end"ed.
  // In either case this is valuable behavior bc it will discourage users from clicking record
  // and pause immediately which is an odd behavior we've observed
  const [isConnecting, setIsConnecting] = useState<boolean>(true);

  const setScribingSessionId = useScribeStore((s) => s.setScribingSessionId);
  const setStep = useScribeStore((s) => s.setStep);

  const [audioContext] = useAudioContext();

  const getScribingSessionIdRef = useSyncedRef(getScribingSessionId);

  const getAuthToken = useCallback(async () => {
    const existing = useScribeStore.getState().scribingSessionId;

    if (existing) {
      return existing;
    }

    const sessionId = await getScribingSessionIdRef.current();

    setScribingSessionId(sessionId);

    return sessionId;
  }, [setScribingSessionId, getScribingSessionIdRef]);

  const scribeClient = useScribeStore((s) => s.scribe);

  const { audioSource, mediaRecorder } = useAudioRecordingComponents(
    async function (event) {
      const scribeClient = useScribeStore.getState().scribe;

      if (!scribeClient) return;

      try {
        console.log("dataavailable", event);
        const buffers = await processScribeRecorderData(
          this,
          scribeClient,
          event,
        );
        buffers.forEach((buf) => {
          scribeClient.send(buf);
        });
      } catch (err) {
        throttledLogError(
          "Error sending media recorder data via scribe client",
          undefined,
          err,
        );
      }
    },
  );

  const recordingOrchestratorRef = useRef(new PromiseOrchestrator());

  const { processTranscriptItem, saveNonFinalizedTranscriptItems } =
    useTranscriptItems(recordingOrchestratorRef, {
      saveTranscriptItem,
      lastOffset,
    });

  const processTranscriptItemRef = useSyncedRef(processTranscriptItem);

  const saveNonFinalizedTranscriptItemsRef = useSyncedRef(
    saveNonFinalizedTranscriptItems,
  );

  const startRecording = async () => {
    try {
      if (!mediaRecorder) {
        throw new Error("Unable to start recording: no media recorder");
      }
      if (!scribeClient) {
        throw new Error("Unable to start recording: scribe client not ready");
      }

      scribeClient.setConfig({ language });
      scribeClient.open();
      mediaRecorder.start(SCRIBE_RECORDER_TIMESLICE_MS);

      setStep("recordingActive");
    } catch (err) {
      scribeLogger.error("Failed to start recording", {
        error: err,
      });
      setStep("initial");
    }
  };

  const stopRecording = async () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }

    if (scribeClient) {
      await scribeClient.close();
    }
  };

  const pauseRecording = async () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers read latest work via useSyncedRef; listing ref.current would churn listeners
  useEffect(
    function attachScribeClientListeners() {
      if (!scribeClient) return;

      scribeClient.on("stateChanged", (state: StringifiedStates) => {
        setIsSafeToResume(state !== "connected.readyToClose");
        setIsConnecting(state.startsWith("connecting"));
      });

      scribeClient.on("transcriptItem", async (item: NablaTranscriptItem) => {
        try {
          console.log("transcriptItem", item);
          await processTranscriptItemRef.current(item);
        } catch (err) {
          console.error("Failed to save transcript item", err);
          setStep("errorNoRetry");
        }
      });

      scribeClient.on("disconnected", (code?: number, reason?: string) => {
        scribeLogger.warn("Scribe client disconnect", {
          code,
          scribingSessionId:
            useScribeStore.getState().scribingSessionId ?? null,
          reason,
        });
        saveNonFinalizedTranscriptItemsRef.current();
      });

      scribeClient.on("error", (err: Error) => {
        scribeLogger.error("Scribe client error", {
          message: err.message,
          scribingSessionId:
            useScribeStore.getState().scribingSessionId ?? null,
        });
        setStep("errorNoRetry");
      });
    },
    [scribeClient],
  );

  useOnStepChange({
    recordingInitializing: async () => {
      await startRecording();
    },
    recordingActive: () => {
      return void 0;
    },
    paused: async () => {
      await pauseRecording();
    },
    finishing: async () => {
      scribeLogger.info("Stopping recording", {
        scribingSessionId: useScribeStore.getState().scribingSessionId ?? null,
      });

      await stopRecording();

      scribeLogger.info("Finishing recording", {
        scribingSessionId: useScribeStore.getState().scribingSessionId ?? null,
      });

      // try to wait for pending websocket messages & transcript item saves
      // to finish before moving on b/c:
      // - we want the note generation step to know about all transcript items
      // - we don't want to patch the scribing session to 'finished' while
      // transcript items are still in-flight because finished scribing
      // sessions will reject all transcript item saves
      await recordingOrchestratorRef.current.waitForAllSettled(30000);

      scribeLogger.info("Finished recording", {
        scribingSessionId: useScribeStore.getState().scribingSessionId ?? null,
      });

      setStep("finished");

      setScribingSessionId(undefined);
    },
  });

  const scribeSetupOptions = useMemo(
    () => ({ getAuthToken, websocketUrl }),
    [getAuthToken, websocketUrl],
  );

  useSetupScribe(scribeSetupOptions);

  return {
    el8TranscribeSessionId: null,
    audioContext,
    audioSource,
    isSafeToResume,
    scribeClient,
    isConnecting,
    startRecording: (sessionId: string) => {
      setScribingSessionId(sessionId);

      setStep("recordingInitializing");
    },
    stopRecording: () => {
      setStep("finishing");
    },
    pauseRecording: () => {
      setStep("paused");
    },
  };
}
